import axios from 'axios';

const API_URL = process.env.REACT_APP_API_BASE_URL
  ? `${process.env.REACT_APP_API_BASE_URL}/api/v1`
  : '/api/v1';

// Get auth header from localStorage
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const messagingApi = {
  /**
   * Get list of message templates
   * @param {Object} filters - Filter parameters (category, active, channel)
   * @returns {Promise} Response with templates array
   */
  getTemplates: async (filters = {}) => {
    const response = await axios.get(`${API_URL}/messaging/templates`, {
      headers: getAuthHeader(),
      params: filters
    });
    return response.data;
  },

  /**
   * Get single template by ID
   * @param {String} id - Template ID
   * @returns {Promise} Response with template object
   */
  getTemplate: async (id) => {
    const response = await axios.get(`${API_URL}/messaging/templates/${id}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  /**
   * Get template statistics
   * @param {String} id - Template ID
   * @returns {Promise} Response with template stats
   */
  getTemplateStats: async (id) => {
    const response = await axios.get(`${API_URL}/messaging/templates/${id}/stats`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  /**
   * Update existing template (admin only)
   * @param {String} id - Template ID
   * @param {Object} payload - Fields to update
   * @returns {Promise} Response with updated template
   */
  updateTemplate: async (id, payload) => {
    const response = await axios.put(
      `${API_URL}/messaging/templates/${id}`,
      payload,
      { headers: getAuthHeader() }
    );
    return response.data;
  },

  /**
   * Send test message using template
   * @param {String} id - Template ID
   * @param {Object} testData - Test message data (recipientPhone, variables, language)
   * @returns {Promise} Response with message ID
   */
  sendTestMessage: async (id, testData) => {
    const response = await axios.post(
      `${API_URL}/messaging/templates/${id}/test`,
      testData,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  },

  /**
   * Get message queue with optional filters
   * @param {Object} filters - Filter parameters (status, channel, startDate, endDate, page, limit)
   * @returns {Promise} Response with messages array and pagination
   */
  getQueue: async (filters = {}) => {
    const response = await axios.get(`${API_URL}/messaging/queue`, {
      headers: getAuthHeader(),
      params: filters
    });
    return response.data;
  },

  /**
   * Get single queued message by ID
   * @param {String} id - Message ID
   * @returns {Promise} Response with message object
   */
  getQueuedMessage: async (id) => {
    const response = await axios.get(`${API_URL}/messaging/queue/${id}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  /**
   * Send manual message
   * @param {Object} messageData - Message data (templateId, recipientPhone, recipientName, variables, channel, scheduledFor, propertyId)
   * @returns {Promise} Response with message ID
   */
  sendMessage: async (messageData) => {
    const response = await axios.post(`${API_URL}/messaging/send`, messageData, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  /**
   * Cancel pending message
   * @param {String} id - Message ID
   * @returns {Promise} Response with success status
   */
  cancelMessage: async (id) => {
    const response = await axios.delete(`${API_URL}/messaging/queue/${id}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  /**
   * Retry failed message (Admin only)
   * @param {String} id - Message ID
   * @returns {Promise} Response with success status
   */
  retryMessage: async (id) => {
    const response = await axios.post(
      `${API_URL}/messaging/queue/${id}/retry`,
      {},
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  },

  /**
   * Get messaging statistics
   * @param {Object} filters - Filter parameters (startDate, endDate)
   * @returns {Promise} Response with stats object
   */
  getStats: async (filters = {}) => {
    const response = await axios.get(`${API_URL}/messaging/stats`, {
      headers: getAuthHeader(),
      params: filters
    });
    return response.data;
  },

  /**
   * Get scheduler status (Admin only)
   * @returns {Promise} Response with scheduler status
   */
  getSchedulerStatus: async () => {
    const response = await axios.get(`${API_URL}/messaging/scheduler/status`, {
      headers: getAuthHeader()
    });
    return response.data;
  }
};

export default messagingApi;
